import * as stylex from "@stylexjs/stylex";
import type { CSSProperties, ReactNode } from "react";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, radii, shadows, space, text } from "@/design/tokens.stylex";

// Skeletons that keep the final layout (Mercury 10). Every surface that waits on
// data uses these primitives: shimmering blocks the size of what will arrive, an
// `aria-busy` region with an assistive-only label, and a quiet polite-live
// "Updating…" chip when cached data stays on screen through a refetch.

const shimmer = "xf4slbi-B";
const pulse = "x3q3iw1-B";
const styles = {
  block: {
    k1xSpc: "x1lliihq",
    kZKoxP: "x1kpxq89",
    kaIpWk: "x1wpy2cq",
    kKwaWg: "x4s867h",
    kgSjnq: "xakli9p",
    kKVMdj: "xkbuz4a x1aquc0h",
    k44tkh: "x1sbju2s",
    kyAemX: "x1esw782",
    ko0y90: "xa4qsjk",
    $$css: true
  },
  round: {
    kaIpWk: "x3kktt9",
    $$css: true
  },
  region: {
    k1xSpc: "x78zum5",
    k7Eaqz: "xeuugli",
    kXwgrk: "xdt5ytf",
    kOIVth: "x5vihii",
    $$css: true
  },
  toolbarChip: {
    kZKoxP: "x5esxvw",
    kzqmXN: "xja02kk",
    kaIpWk: "x16l1o21",
    $$css: true
  },
  bandLabel: {
    kzqmXN: "x13oubkp",
    kZKoxP: "x170jfvy",
    $$css: true
  },
  bandValue: {
    kzqmXN: "x2pejg6",
    kZKoxP: "x17rw0jw",
    $$css: true
  },
  headerCell: {
    kZKoxP: "xegnrdp",
    kzqmXN: "x1kmanbg",
    $$css: true
  },
  cell: {
    kZKoxP: "x1kpxq89",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  pageTitle: {
    kZKoxP: "x1fgtraw",
    kzqmXN: "x16grhtn",
    $$css: true
  },
  pageTabs: {
    kZKoxP: "xlup9mm",
    kzqmXN: "x1m258z3",
    $$css: true
  }
};
interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  /** Round ends, for a pill or chip placeholder. */
  round?: boolean;
  xstyle?: stylex.StyleXStyles;
}

/** One shimmering block. Decorative: hidden from assistive technology. */
export function Skeleton({
  width,
  height,
  round = false,
  xstyle
}: SkeletonProps) {
  const props = stylex.props(styles.block, round && styles.round, xstyle);
  return <span aria-hidden="true" data-skeleton className={props.className} style={{
    width,
    height
  }} />;
}
interface LoadingRegionProps {
  /** Assistive-only description, e.g. "Loading conversions". */
  label: string;
  xstyle?: stylex.StyleXStyles;
  children: ReactNode;
}

/**
 * The container around any skeleton: marks the region busy for assistive
 * technology, exposes a visually hidden label, and hides the decorative
 * blocks inside it. Focus is never moved.
 */
export function LoadingRegion({
  label,
  xstyle,
  children
}: LoadingRegionProps) {
  return <div role="status" aria-busy="true" aria-live="polite" data-loading-region {...stylex.props(styles.region, xstyle)}>
      <span {...stylex.props(recipes.srOnly)}>{label}…</span>
      <div aria-hidden="true" className="x78zum5 xeuugli xdt5ytf x5vihii">
        {children}
      </div>
    </div>;
}
interface TableSkeletonProps {
  label: string;
  rows?: number;
  columns?: number;
  /** Render the chip row above the table. */
  toolbar?: boolean;
  /** Render a summary band above the table. */
  summary?: number;
  xstyle?: stylex.StyleXStyles;
}

/**
 * A table-shaped skeleton: an optional chip row and summary band, then a
 * header row and `rows` body rows at the loaded table's row height, so content
 * arriving does not shift the page.
 */
export function TableSkeleton({
  label,
  rows = 8,
  columns = 4,
  toolbar = false,
  summary = 0,
  xstyle
}: TableSkeletonProps) {
  const cellCount = columns + 1;
  return <LoadingRegion label={label} xstyle={xstyle}>
      {toolbar && <div className="x78zum5 x1a02dak x166cprz">
          <Skeleton xstyle={styles.toolbarChip} />
          <Skeleton xstyle={styles.toolbarChip} width={84} />
          <Skeleton xstyle={styles.toolbarChip} width={96} />
        </div>}
      {summary > 0 && <div className="x78zum5 xwsoklo x9end0a">
          {Array.from({
        length: summary
      }, (_, index) => <div key={index} className="x78zum5 xdt5ytf x166cprz">
              <Skeleton xstyle={styles.bandLabel} />
              <Skeleton xstyle={styles.bandValue} />
            </div>)}
        </div>}
      <div className="x78zum5 xdt5ytf" style={{
      "--skeleton-cols": columns
    } as CSSProperties}>
        <div className="xrvj5dj xpw1ped x1l51sop x6s0dn4 xso031l x1q0q8m5 x1eryijn xaecqrk">
          {Array.from({
          length: cellCount
        }, (_, index) => <Skeleton key={index} xstyle={styles.headerCell} />)}
        </div>
        {Array.from({
        length: rows
      }, (_, rowIndex) => <div key={rowIndex} className="xrvj5dj xpw1ped x1l51sop x6s0dn4 xdxhgn1 xso031l x1q0q8m5 xm50ttx">
            {Array.from({
          length: cellCount
        }, (_, index) => <Skeleton key={index} xstyle={styles.cell} />)}
          </div>)}
      </div>
    </LoadingRegion>;
}

/**
 * Route-level pending page used by the router's `defaultPendingComponent`:
 * a title and tab strip over a table, matching the page layout.
 */
export function PendingPage({
  label = "Loading"
}: {
  label?: string;
}) {
  return <div className="x78zum5 xdt5ytf x1dfszw7 x13q3zuf xkkwws9">
      <LoadingRegion label={label}>
        <Skeleton xstyle={styles.pageTitle} />
        <Skeleton xstyle={styles.pageTabs} />
      </LoadingRegion>
      <TableSkeleton label={label} toolbar />
    </div>;
}
interface UpdatingChipProps {
  /** Show the chip. The polite live region stays mounted either way. */
  active: boolean;
  label?: string;
  /**
   * `floating` overlays the chip on the top-right of the following content
   * without taking layout space; `inline` renders it in flow.
   */
  placement?: "floating" | "inline";
}

/**
 * The quiet refetch indicator: rendered while cached data stays visible and
 * a newer result is on its way (`isFetching && !isLoading`, or placeholder
 * data). The live region is always mounted so the announcement is polite and
 * never steals focus; the visible chip only appears while `active`.
 */
export function UpdatingChip({
  active,
  label = "Updating…",
  placement = "floating"
}: UpdatingChipProps) {
  const floating = placement === "floating";
  return <div role="status" aria-live="polite" {...{
    0: {
      className: "x1rg5ohu"
    },
    1: {
      className: "x1n2onr6 x1lliihq xqtp20y x1rea2x4 x1355qak"
    }
  }[!!floating << 0]}>
      {active ? <span data-updating-chip {...{
      0: {
        className: "x3nfvp2 x6s0dn4 xsuxsxl xifx94n xtft4pz x3kktt9 x2jzp15 x1sen6cn x1hqrb6m xo5v014 x1dgl1gp xuxw1ft"
      },
      1: {
        className: "x3nfvp2 x6s0dn4 xsuxsxl xifx94n xtft4pz x3kktt9 x2jzp15 x1sen6cn x1hqrb6m xo5v014 x1dgl1gp xuxw1ft x10l6tqk x1eu8d0j x2i5vmh"
      }
    }[!!floating << 0]}>
          <span aria-hidden="true" className="x1v4s8kt xols6we x3kktt9 x1wg9yre x194e4vk x1aquc0h x1m9vv7p x4hg4is xa4qsjk" />
          {label}
        </span> : null}
    </div>;
}